import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { MessageCircle, X, Send, Loader2, Sparkles } from "lucide-react";

const GREEN = "#14532D";

function renderText(t) {
  // negrita *texto* -> <b>
  const parts = String(t || "").split(/(\*[^*]+\*)/g);
  return parts.map((p, i) =>
    p.startsWith("*") && p.endsWith("*") && p.length > 2
      ? <b key={i}>{p.slice(1, -1)}</b>
      : <span key={i}>{p}</span>
  );
}

export default function AssistantWidget() {
  const [enabled, setEnabled] = useState(false);
  const [open, setOpen] = useState(false);
  const [sugs, setSugs] = useState([]);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [iaLeft, setIaLeft] = useState(null);
  const endRef = useRef(null);
  const { user } = useAuth();

  useEffect(() => {
    if (!user || typeof user !== "object") { setEnabled(false); return; }
    api.get("/assistant/suggestions")
      .then(({ data }) => {
        setEnabled(true);
        setSugs(data.suggestions || []);
        setIaLeft(data.ia_left);
        setMsgs([{ role: "bot", text: `¡Hola${data.nombre ? " " + data.nombre.split(" ")[0] : ""}! 👋 Soy tu asistente de Granos. Tocá una opción o escribime.` }]);
      })
      .catch(() => setEnabled(false));
  }, [user]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, open]);

  const send = async (text, label) => {
    const t = (text ?? input).trim();
    if (!t || busy) return;
    setInput("");
    setMsgs((m) => [...m, { role: "user", text: label || t }]);
    setBusy(true);
    try {
      const { data } = await api.post("/assistant/chat", { message: t, channel: "web" });
      setIaLeft(data.ia_left);
      setMsgs((m) => [...m, { role: "bot", text: data.reply, media: data.media || [], ia: data.ia_used, buttons: data.buttons || [], list: data.list || null }]);
    } catch (e) {
      setMsgs((m) => [...m, { role: "bot", text: "No pude procesar eso. Probá de nuevo o usá los botones." }]);
    } finally {
      setBusy(false);
    }
  };

  if (!enabled) return null;

  return (
    <div style={{ fontFamily: "Manrope, sans-serif" }}>
      {!open && (
        <button
          data-testid="assistant-fab"
          onClick={() => setOpen(true)}
          style={{ position: "fixed", right: 20, bottom: 20, zIndex: 1000, height: 48, width: 48,
            borderRadius: 999, background: GREEN, color: "#fff", border: "none", boxShadow: "0 10px 30px rgba(0,0,0,.25)",
            display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}
          title="Ayuda"
        >
          <MessageCircle size={22} />
        </button>
      )}

      {open && (
        <div data-testid="assistant-panel"
          style={{ position: "fixed", right: 16, bottom: 16, zIndex: 1000, width: "min(92vw, 390px)",
            height: "min(76vh, 620px)", background: "#f4f7f4", borderRadius: 20, overflow: "hidden",
            display: "flex", flexDirection: "column", boxShadow: "0 24px 60px rgba(0,0,0,.35)" }}>
          <div style={{ background: GREEN, color: "#fff", padding: "14px 16px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <Sparkles size={20} />
              <div>
                <div style={{ fontWeight: 800, fontSize: 15 }}>Asistente Granos</div>
                {iaLeft != null && <div style={{ fontSize: 11, color: "#bfe3cc" }}>{iaLeft} consultas IA hoy</div>}
              </div>
            </div>
            <button data-testid="assistant-close" onClick={() => setOpen(false)}
              style={{ background: "transparent", border: "none", color: "#fff", cursor: "pointer", height: 44, width: 44 }}>
              <X size={22} />
            </button>
          </div>

          <div style={{ flex: 1, overflowY: "auto", padding: 14, display: "flex", flexDirection: "column", gap: 10 }}>
            {msgs.map((m, i) => (
              <div key={i} style={{ alignSelf: m.role === "user" ? "flex-end" : "flex-start", maxWidth: "88%" }}>
                <div style={{ background: m.role === "user" ? GREEN : "#fff", color: m.role === "user" ? "#fff" : "#1b2a20",
                  borderRadius: 16, padding: "10px 13px", fontSize: 14, lineHeight: 1.45, whiteSpace: "pre-wrap",
                  boxShadow: "0 2px 8px rgba(0,0,0,.06)" }}>
                  {renderText(m.text)}
                  {(m.media || []).map((u, k) => (
                    <div key={k} style={{ marginTop: 8 }}>
                      <a href={u} target="_blank" rel="noreferrer" style={{ color: m.role === "user" ? "#d7f5c8" : GREEN, fontWeight: 700, fontSize: 13 }}>📎 Ver / descargar</a>
                    </div>
                  ))}
                  {m.ia && <div style={{ marginTop: 6, fontSize: 10, color: "#8aa090" }}>🤖 con IA</div>}
                </div>
                {((m.buttons && m.buttons.length) || (m.list && m.list.items && m.list.items.length)) && i === msgs.length - 1 && (
                  <div data-testid="assistant-options" style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 8 }}>
                    {(m.buttons || []).map((b, k) => (
                      <button key={`b${k}`} data-testid={`assistant-opt-${b.id}`} onClick={() => send(b.id, b.title)} disabled={busy}
                        style={{ background: GREEN, color: "#fff", border: "none", borderRadius: 999, padding: "9px 14px", fontSize: 13, fontWeight: 700, cursor: "pointer", minHeight: 40 }}>
                        {b.title}
                      </button>
                    ))}
                    {(m.list?.items || []).map((it, k) => (
                      <button key={`l${k}`} data-testid={`assistant-opt-${it.id}`} onClick={() => send(it.id, it.title)} disabled={busy}
                        style={{ background: "#fff", border: `1.5px solid ${GREEN}`, color: GREEN, borderRadius: 12, padding: "9px 13px", fontSize: 13, fontWeight: 700, cursor: "pointer", minHeight: 40, textAlign: "left" }}>
                        {it.title}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {busy && <div style={{ alignSelf: "flex-start", color: GREEN }}><Loader2 className="animate-spin" size={18} /></div>}
            <div ref={endRef} />
          </div>

          {msgs.length <= 1 && (
            <div style={{ padding: "0 12px 8px", display: "flex", flexWrap: "wrap", gap: 8 }}>
              {sugs.map((s, i) => (
                <button key={i} data-testid={`assistant-sug-${i}`} onClick={() => send(s.send, s.label)}
                  style={{ background: "#fff", border: `1.5px solid ${GREEN}22`, color: GREEN, borderRadius: 999,
                    padding: "9px 13px", fontSize: 13, fontWeight: 700, cursor: "pointer", minHeight: 40 }}>
                  {s.label}
                </button>
              ))}
            </div>
          )}

          <div style={{ padding: 12, display: "flex", gap: 8, background: "#fff", borderTop: "1px solid #e4ece5" }}>
            <input data-testid="assistant-input" value={input} onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Escribí tu consulta…"
              style={{ flex: 1, border: "1px solid #d7e2d9", borderRadius: 12, padding: "0 14px", height: 44, fontSize: 14, outline: "none" }} />
            <button data-testid="assistant-send" onClick={() => send()} disabled={busy}
              style={{ background: GREEN, color: "#fff", border: "none", borderRadius: 12, height: 44, width: 44, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Send size={18} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
